/** The account, order and risk rows and the step-up answer, mirroring `ssi.api.views_account` (INC-0609). */
import {
  COMMAND_ACTIONS,
  ORDER_STATES,
  decodeCommandRequest,
  type CommandAction,
  type CommandRequest,
  type OrderState,
} from "./commands.ts";
import {
  ContractViolation,
  bool,
  exact,
  field,
  instant,
  listOf,
  nat,
  nullable,
  oneOf,
  paise,
  record,
  str,
  type Reader,
} from "./decode.ts";
import { ENVIRONMENTS } from "./envelope.ts";
import { LATCHES, SIDES } from "./views.ts";

/** The account projections `ssi.api.views_account.VIEW_SCHEMAS_ACCOUNT` declares. */
export const ACCOUNT_VIEWS: readonly string[] = [
  "balances",
  "exposure",
  "latches",
  "limits",
  "orders",
  "positions",
  "reconciliation",
  "risk_controls",
];
/** The longest reason a command may state (`ssi.api.commands.REASON_MAX`). */
export const REASON_MAX = 2000;
/** `POST` here to obtain a single-use authorisation for a step-up action. */
export const STEP_UP_PATH = "/api/v1/step-up";
/** `POST` here asks for an out-of-band code through the caller's own channel (INC-0807 server half). */
export const OUT_OF_BAND_PATH = "/api/v1/commands/out-of-band";

export const POSITION_STATUSES = ["OPEN", "CLOSED", "UNKNOWN"] as const;
export const PROTECTIONS = [
  "PROTECTED",
  "PARTIAL",
  "UNPROTECTED",
  "UNKNOWN",
  "NOT_APPLICABLE",
] as const;
export const FILL_STATES = ["CONFIRMED", "PARTIAL", "UNKNOWN"] as const;
export const FILL_SOURCES = ["BROKER_REPORTED", "MANUAL", "SIMULATED"] as const;
export const CONCENTRATION_DIMENSIONS = ["NAME", "SECTOR", "CATALYST"] as const;
export const LIMIT_KINDS = ["DAILY_LOSS", "PLANNED_RISK", "STRESSED_RISK", "DEADLINE"] as const;
export const ORDER_ROLES = ["ENTRY", "PROTECTIVE_STOP", "EXIT"] as const;
export const DIFFERENCE_KINDS = [
  "MISSING_FILL",
  "DUPLICATE_FILL",
  "QUANTITY_DIFFERENCE",
  "UNEXPLAINED_ORDER",
] as const;
export const RESOLUTIONS = ["OPEN", "REQUESTED", "RESOLVED"] as const;
export const CONTROL_KINDS = ["ENTRIES", "EXECUTION_OWNER", "MANDATE", "READINESS"] as const;
export const CONTROL_STATES = ["ENABLED", "PAUSED", "MANUAL", "BLOCKED"] as const;

type Of<T extends readonly string[]> = T[number];

function refuse(path: string, expected: string): never {
  throw new ContractViolation(path, expected);
}

/** One position (U037); an UNKNOWN or partial position is never closed (UX04). */
export interface PositionRow {
  readonly instrument: string;
  readonly side: Of<typeof SIDES>;
  readonly status: Of<typeof POSITION_STATUSES>;
  readonly acquiredQuantity: number;
  /** `null` is unknown, never zero. */
  readonly residualQuantity: number | null;
  readonly averageEntryPaise: bigint | null;
  readonly protection: Of<typeof PROTECTIONS>;
  readonly realisedPnlPaise: bigint;
  readonly unrealisedPnlPaise: bigint | null;
  readonly markPaise: bigint | null;
  readonly markAt: string | null;
  readonly fillState: Of<typeof FILL_STATES>;
  readonly source: Of<typeof FILL_SOURCES>;
  readonly version: number;
}

/** Decode a `positions` row; the contradictions the Python class refuses fail closed here too. */
export function decodePositionRow(value: unknown, path = "position"): PositionRow {
  const f = exact(value, path, [
    "instrument",
    "side",
    "status",
    "acquired_quantity",
    "residual_quantity",
    "average_entry_paise",
    "protection",
    "realised_pnl_paise",
    "unrealised_pnl_paise",
    "mark_paise",
    "mark_at",
    "fill_state",
    "source",
    "version",
  ]);
  const row: PositionRow = {
    instrument: field(f, path, "instrument", str),
    side: field(f, path, "side", oneOf(SIDES)),
    status: field(f, path, "status", oneOf(POSITION_STATUSES)),
    acquiredQuantity: field(f, path, "acquired_quantity", nat),
    residualQuantity: field(f, path, "residual_quantity", nullable(nat)),
    averageEntryPaise: field(f, path, "average_entry_paise", nullable(paise)),
    protection: field(f, path, "protection", oneOf(PROTECTIONS)),
    realisedPnlPaise: field(f, path, "realised_pnl_paise", paise),
    unrealisedPnlPaise: field(f, path, "unrealised_pnl_paise", nullable(paise)),
    markPaise: field(f, path, "mark_paise", nullable(paise)),
    markAt: field(f, path, "mark_at", nullable(instant)),
    fillState: field(f, path, "fill_state", oneOf(FILL_STATES)),
    source: field(f, path, "source", oneOf(FILL_SOURCES)),
    version: field(f, path, "version", nat),
  };
  if ((row.residualQuantity === null) !== (row.fillState === "UNKNOWN")) {
    refuse(path, "an unknown residual exactly when the fill state is UNKNOWN");
  }
  if ((row.status === "UNKNOWN") !== (row.residualQuantity === null)) {
    refuse(path, "status UNKNOWN exactly when the residual is unknown");
  }
  if (row.status === "CLOSED" && (row.residualQuantity !== 0 || row.fillState !== "CONFIRMED")) {
    refuse(path, "a closed position with nothing left and confirmed fills");
  }
  if (row.status === "OPEN" && row.residualQuantity === 0) {
    refuse(path, "an open position with a residual");
  }
  return row;
}

/** The account's balances, fees and outcomes (U036, U039). */
export interface BalanceRow {
  /** The account aggregate version: the expected state version of every command. */
  readonly accountVersion: number;
  readonly cashPaise: bigint;
  readonly equityPaise: bigint;
  readonly marginUsedPaise: bigint;
  readonly collateralPaise: bigint;
  readonly reservedPaise: bigint;
  readonly availablePaise: bigint;
  readonly accountAt: string;
  readonly valuationAt: string;
  readonly estimatedFeesPaise: bigint;
  readonly reconciledFeesPaise: bigint | null;
  readonly realisedObservedPaise: bigint;
  readonly realisedSimulatedPaise: bigint;
  readonly markedPaise: bigint;
  readonly stressPaise: bigint | null;
}

/** Decode a `balances` row. */
export function decodeBalanceRow(value: unknown, path = "balance"): BalanceRow {
  const f = exact(value, path, [
    "account_version",
    "cash_paise",
    "equity_paise",
    "margin_used_paise",
    "collateral_paise",
    "reserved_paise",
    "available_paise",
    "account_at",
    "valuation_at",
    "estimated_fees_paise",
    "reconciled_fees_paise",
    "realised_observed_paise",
    "realised_simulated_paise",
    "marked_paise",
    "stress_paise",
  ]);
  return {
    accountVersion: field(f, path, "account_version", nat),
    cashPaise: field(f, path, "cash_paise", paise),
    equityPaise: field(f, path, "equity_paise", paise),
    marginUsedPaise: field(f, path, "margin_used_paise", paise),
    collateralPaise: field(f, path, "collateral_paise", paise),
    reservedPaise: field(f, path, "reserved_paise", paise),
    availablePaise: field(f, path, "available_paise", paise),
    accountAt: field(f, path, "account_at", instant),
    valuationAt: field(f, path, "valuation_at", instant),
    estimatedFeesPaise: field(f, path, "estimated_fees_paise", paise),
    reconciledFeesPaise: field(f, path, "reconciled_fees_paise", nullable(paise)),
    realisedObservedPaise: field(f, path, "realised_observed_paise", paise),
    realisedSimulatedPaise: field(f, path, "realised_simulated_paise", paise),
    markedPaise: field(f, path, "marked_paise", paise),
    stressPaise: field(f, path, "stress_paise", nullable(paise)),
  };
}

/** One exposure bucket against its limit (U038). */
export interface ConcentrationRow {
  readonly dimension: Of<typeof CONCENTRATION_DIMENSIONS>;
  readonly key: string;
  readonly exposurePaise: bigint;
  readonly limitPaise: bigint;
  readonly positionCount: number;
}

/** Decode an `exposure` row. */
export function decodeConcentrationRow(value: unknown, path = "exposure"): ConcentrationRow {
  const f = exact(value, path, [
    "dimension",
    "key",
    "exposure_paise",
    "limit_paise",
    "position_count",
  ]);
  return {
    dimension: field(f, path, "dimension", oneOf(CONCENTRATION_DIMENSIONS)),
    key: field(f, path, "key", str),
    exposurePaise: field(f, path, "exposure_paise", paise),
    limitPaise: field(f, path, "limit_paise", paise),
    positionCount: field(f, path, "position_count", nat),
  };
}

/** A limit or deadline and why it blocks (U038, U050). */
export interface LimitRow {
  readonly limitId: string;
  readonly label: string;
  readonly kind: Of<typeof LIMIT_KINDS>;
  readonly usedPaise: bigint | null;
  readonly limitPaise: bigint | null;
  readonly deadline: string | null;
  readonly policyVersion: string;
  readonly explanation: string;
  readonly blocking: boolean;
}

/** Decode a `limits` row. */
export function decodeLimitRow(value: unknown, path = "limit"): LimitRow {
  const f = exact(value, path, [
    "limit_id",
    "label",
    "kind",
    "used_paise",
    "limit_paise",
    "deadline",
    "policy_version",
    "explanation",
    "blocking",
  ]);
  const row: LimitRow = {
    limitId: field(f, path, "limit_id", str),
    label: field(f, path, "label", str),
    kind: field(f, path, "kind", oneOf(LIMIT_KINDS)),
    usedPaise: field(f, path, "used_paise", nullable(paise)),
    limitPaise: field(f, path, "limit_paise", nullable(paise)),
    deadline: field(f, path, "deadline", nullable(instant)),
    policyVersion: field(f, path, "policy_version", str),
    explanation: field(f, path, "explanation", str),
    blocking: field(f, path, "blocking", bool),
  };
  if ((row.usedPaise === null) !== (row.limitPaise === null)) {
    refuse(path, "used_paise and limit_paise both present or both absent");
  }
  if (row.limitPaise === null && row.deadline === null) {
    refuse(path, "a limit or a deadline");
  }
  return row;
}

/** One order in a parent and child tree: the broker or execution lifecycle (U041, U042). */
export interface OrderRow {
  readonly orderId: string;
  readonly parentOrderId: string | null;
  readonly instrument: string;
  readonly side: Of<typeof SIDES>;
  readonly role: Of<typeof ORDER_ROLES>;
  readonly state: OrderState;
  readonly quantity: number;
  readonly filledQuantity: number;
  /** `null` when the state is UNKNOWN: never zero. */
  readonly residualQuantity: number | null;
  readonly limitPaise: bigint | null;
  readonly updatedAt: string;
  readonly version: number;
}

/** Decode an `orders` row; the quantity rules the Python class enforces fail closed here too. */
export function decodeOrderRow(value: unknown, path = "order"): OrderRow {
  const f = exact(value, path, [
    "order_id",
    "parent_order_id",
    "instrument",
    "side",
    "role",
    "state",
    "quantity",
    "filled_quantity",
    "residual_quantity",
    "limit_paise",
    "updated_at",
    "version",
  ]);
  const row: OrderRow = {
    orderId: field(f, path, "order_id", str),
    parentOrderId: field(f, path, "parent_order_id", nullable(str)),
    instrument: field(f, path, "instrument", str),
    side: field(f, path, "side", oneOf(SIDES)),
    role: field(f, path, "role", oneOf(ORDER_ROLES)),
    state: field(f, path, "state", oneOf(ORDER_STATES)),
    quantity: field(f, path, "quantity", nat),
    filledQuantity: field(f, path, "filled_quantity", nat),
    residualQuantity: field(f, path, "residual_quantity", nullable(nat)),
    limitPaise: field(f, path, "limit_paise", nullable(paise)),
    updatedAt: field(f, path, "updated_at", instant),
    version: field(f, path, "version", nat),
  };
  if (row.quantity < 1 || row.filledQuantity > row.quantity) {
    refuse(path, "a positive quantity and a fill within it");
  }
  if ((row.residualQuantity === null) !== (row.state === "UNKNOWN")) {
    refuse(path, "an unknown residual exactly when the state is UNKNOWN");
  }
  if (row.residualQuantity !== null && row.residualQuantity !== row.quantity - row.filledQuantity) {
    refuse(path, "a residual equal to the quantity less the fill");
  }
  if ((row.state === "FILLED") !== (row.filledQuantity === row.quantity)) {
    refuse(path, "FILLED exactly when the whole quantity is filled");
  }
  return row;
}

/** One internal versus broker difference (U044, U045). */
export interface ReconciliationRow {
  readonly differenceId: string;
  readonly kind: Of<typeof DIFFERENCE_KINDS>;
  readonly instrument: string;
  readonly orderId: string | null;
  readonly internalQuantity: number | null;
  readonly brokerQuantity: number | null;
  readonly detectedAt: string;
  readonly snapshotRef: string;
  readonly resolution: Of<typeof RESOLUTIONS>;
  readonly explanation: string;
}

/** Decode a `reconciliation` row. */
export function decodeReconciliationRow(value: unknown, path = "difference"): ReconciliationRow {
  const f = exact(value, path, [
    "difference_id",
    "kind",
    "instrument",
    "order_id",
    "internal_quantity",
    "broker_quantity",
    "detected_at",
    "snapshot_ref",
    "resolution",
    "explanation",
  ]);
  const row: ReconciliationRow = {
    differenceId: field(f, path, "difference_id", str),
    kind: field(f, path, "kind", oneOf(DIFFERENCE_KINDS)),
    instrument: field(f, path, "instrument", str),
    orderId: field(f, path, "order_id", nullable(str)),
    internalQuantity: field(f, path, "internal_quantity", nullable(nat)),
    brokerQuantity: field(f, path, "broker_quantity", nullable(nat)),
    detectedAt: field(f, path, "detected_at", instant),
    snapshotRef: field(f, path, "snapshot_ref", str),
    resolution: field(f, path, "resolution", oneOf(RESOLUTIONS)),
    explanation: field(f, path, "explanation", str),
  };
  if (
    row.kind === "QUANTITY_DIFFERENCE" &&
    (row.internalQuantity === null ||
      row.brokerQuantity === null ||
      row.internalQuantity === row.brokerQuantity)
  ) {
    refuse(path, "a quantity difference holds two differing quantities");
  }
  return row;
}

/** An operating control: entries, execution owner, mandate or readiness (U046-U049). */
export interface ControlRow {
  readonly controlId: string;
  readonly kind: Of<typeof CONTROL_KINDS>;
  readonly label: string;
  readonly state: Of<typeof CONTROL_STATES>;
  readonly since: string | null;
  readonly scope: string;
  readonly policyVersion: string;
  readonly blockers: readonly string[];
  readonly expiresAt: string | null;
  readonly revoked: boolean;
}

/** Decode a `risk_controls` row. */
export function decodeControlRow(value: unknown, path = "control"): ControlRow {
  const f = exact(value, path, [
    "control_id",
    "kind",
    "label",
    "state",
    "since",
    "scope",
    "policy_version",
    "blockers",
    "expires_at",
    "revoked",
  ]);
  const row: ControlRow = {
    controlId: field(f, path, "control_id", str),
    kind: field(f, path, "kind", oneOf(CONTROL_KINDS)),
    label: field(f, path, "label", str),
    state: field(f, path, "state", oneOf(CONTROL_STATES)),
    since: field(f, path, "since", nullable(instant)),
    scope: field(f, path, "scope", str),
    policyVersion: field(f, path, "policy_version", str),
    blockers: field(f, path, "blockers", listOf(str)),
    expiresAt: field(f, path, "expires_at", nullable(instant)),
    revoked: field(f, path, "revoked", bool),
  };
  if ((row.state === "BLOCKED") !== row.blockers.length > 0) {
    refuse(path, "blockers exactly when BLOCKED");
  }
  if (row.kind !== "MANDATE" && (row.expiresAt !== null || row.revoked)) {
    refuse(path, "only a mandate expires or is revoked");
  }
  return row;
}

/** A safety latch (U049, U050): acknowledging an alert about it releases nothing (UX08). */
export interface LatchRow {
  readonly latchId: string;
  readonly label: string;
  readonly state: "ENGAGED" | "RELEASED";
  readonly engagedAt: string;
  readonly releasedAt: string | null;
  readonly scope: string;
  readonly reason: string;
  readonly acknowledged: boolean;
  readonly releaseRequires: string;
  readonly incidentId: string | null;
}

/** Decode a `latches` row. */
export function decodeLatchRow(value: unknown, path = "latch"): LatchRow {
  const f = exact(value, path, [
    "latch_id",
    "label",
    "state",
    "engaged_at",
    "released_at",
    "scope",
    "reason",
    "acknowledged",
    "release_requires",
    "incident_id",
  ]);
  const state = field(f, path, "state", oneOf(LATCHES));
  const releasedAt = field(f, path, "released_at", nullable(instant));
  const engagedAt = field(f, path, "engaged_at", instant);
  if (state === "NONE") {
    refuse(path, "a latch that is ENGAGED or RELEASED");
  }
  if ((state === "RELEASED") !== (releasedAt !== null)) {
    refuse(path, "a release time exactly when RELEASED");
  }
  if (releasedAt !== null && releasedAt < engagedAt) {
    refuse(path, "a release not before the engagement");
  }
  return {
    latchId: field(f, path, "latch_id", str),
    label: field(f, path, "label", str),
    state,
    engagedAt,
    releasedAt,
    scope: field(f, path, "scope", str),
    reason: field(f, path, "reason", str),
    acknowledged: field(f, path, "acknowledged", bool),
    releaseRequires: field(f, path, "release_requires", str),
    incidentId: field(f, path, "incident_id", nullable(str)),
  };
}

/** The single-use authorisation a step-up answers with; the nonce is held in memory only (S12). */
export interface ActionAuthorisation {
  readonly authorisationId: string;
  readonly nonce: string;
  readonly action: string;
  readonly accountId: string;
  readonly environment: Of<typeof ENVIRONMENTS>;
  readonly payloadDigest: string;
  readonly expectedVersion: number;
  readonly issuedAt: string;
  readonly expiresAt: string;
  readonly securityEpoch: number;
}

/** Decode the `POST /api/v1/step-up` answer. */
export function decodeAuthorisation(value: unknown): ActionAuthorisation {
  const path = "authorisation";
  const f = exact(value, path, [
    "authorisation_id",
    "nonce",
    "action",
    "account_id",
    "environment",
    "payload_digest",
    "expected_version",
    "issued_at",
    "expires_at",
    "security_epoch",
  ]);
  return {
    authorisationId: field(f, path, "authorisation_id", str),
    nonce: field(f, path, "nonce", str),
    action: field(f, path, "action", str),
    accountId: field(f, path, "account_id", str),
    environment: field(f, path, "environment", oneOf(ENVIRONMENTS)),
    payloadDigest: field(f, path, "payload_digest", str),
    expectedVersion: field(f, path, "expected_version", nat),
    issuedAt: field(f, path, "issued_at", instant),
    expiresAt: field(f, path, "expires_at", instant),
    securityEpoch: field(f, path, "security_epoch", nat),
  };
}

/** The row reader of each account projection, by name. */
export const ACCOUNT_ROWS: Readonly<Record<string, Reader<unknown>>> = {
  positions: decodePositionRow,
  balances: decodeBalanceRow,
  exposure: decodeConcentrationRow,
  limits: decodeLimitRow,
  orders: decodeOrderRow,
  reconciliation: decodeReconciliationRow,
  risk_controls: decodeControlRow,
  latches: decodeLatchRow,
};

/** A command request with its single-use proof, as a step-up command goes on the wire. */
export interface AuthorisedRequest {
  readonly request: CommandRequest;
  readonly authorisationId: string;
  readonly nonce: string;
}

/** Decode the wire form of a step-up command: the nine request keys plus `authorisation`. */
export function decodeAuthorisedRequest(value: unknown): AuthorisedRequest {
  const path = "request";
  const { authorisation, ...rest } = record(value, path);
  const f = exact(authorisation, `${path}.authorisation`, ["authorisation_id", "nonce"]);
  return {
    request: decodeCommandRequest(rest),
    authorisationId: field(f, `${path}.authorisation`, "authorisation_id", str),
    nonce: field(f, `${path}.authorisation`, "nonce", str),
  };
}

// --- manual and broker-originated fills (INC-0707, U040; mirrors `ssi.account.manual_fills`) ---

/** Import (`POST`), list (`GET ?account_id=`) and inspect (`GET /{fill_id}`) recorded fills. */
export const FILLS_PATH = "/api/v1/fills";
export const FILL_SIDES = ["BUY", "SELL"] as const;
export const FILL_SOURCE_KINDS = [
  "MANUAL_ENTRY",
  "BROKER_TRADE_BOOK",
  "BROKER_CONTRACT_NOTE",
] as const;
export const RECONCILIATION_STATES = ["UNRECONCILED", "RECONCILED", "MISMATCHED"] as const;
/** The evidence tag of a fill: a simulated and an observed fill are never added together. */
export const EVIDENCE_TAGS = ["SIMULATED", "OBSERVED"] as const;
export const FILL_TERMS = ["QUANTITY", "PRICE", "FEES"] as const;

const FILL_ID = /^[0-9a-f]{32}$/;
const SHA256 = /^[0-9a-f]{64}$/;

/** One term on which a fill and its broker-statement record disagree: broker minus recorded. */
export interface TermDifference {
  readonly term: Of<typeof FILL_TERMS>;
  readonly recorded: bigint;
  readonly broker: bigint;
  readonly difference: bigint;
}

function decodeDifference(value: unknown, path: string): TermDifference {
  const f = exact(value, path, ["term", "recorded", "broker", "difference"]);
  const row: TermDifference = {
    term: field(f, path, "term", oneOf(FILL_TERMS)),
    recorded: field(f, path, "recorded", paise),
    broker: field(f, path, "broker", paise),
    difference: field(f, path, "difference", signedInteger),
  };
  if (row.difference === 0n || row.difference !== row.broker - row.recorded) {
    refuse(path, "a non-zero difference equal to broker minus recorded");
  }
  return row;
}

function signedInteger(value: unknown, path: string): bigint {
  if (typeof value === "number" && Number.isSafeInteger(value)) {
    return BigInt(value);
  }
  return refuse(path, "a signed integer");
}

function matching(pattern: RegExp, expected: string): Reader<string> {
  return (value, path) => {
    const text = str(value, path);
    return pattern.test(text) ? text : refuse(path, expected);
  };
}

/** A recorded fill with its provenance and reconciliation (U040). */
export interface FillRecord {
  readonly fillId: string;
  readonly accountId: string;
  readonly listingId: string;
  readonly side: Of<typeof FILL_SIDES>;
  readonly quantity: number;
  readonly pricePaise: bigint;
  readonly feesPaise: bigint;
  readonly evidence: Of<typeof EVIDENCE_TAGS>;
  readonly sourceKind: Of<typeof FILL_SOURCE_KINDS>;
  readonly documentHash: string;
  readonly operator: string;
  readonly recordedAt: string;
  readonly exchangeTime: string;
  readonly brokerReference: string | null;
  readonly reconciliation: Of<typeof RECONCILIATION_STATES>;
  readonly statementId: string | null;
  readonly differences: readonly TermDifference[];
  readonly version: number;
}

/** Decode a fill record; a reconciliation that contradicts its statement or differences fails closed. */
export function decodeFillRecord(value: unknown, path = "fill"): FillRecord {
  const f = exact(value, path, [
    "fill_id",
    "account_id",
    "listing_id",
    "side",
    "quantity",
    "price_paise",
    "fees_paise",
    "evidence",
    "source_kind",
    "document_hash",
    "operator",
    "recorded_at",
    "exchange_time",
    "broker_reference",
    "reconciliation",
    "statement_id",
    "differences",
    "version",
  ]);
  const row: FillRecord = {
    fillId: field(f, path, "fill_id", matching(FILL_ID, "32 hexadecimal digits")),
    accountId: field(f, path, "account_id", str),
    listingId: field(f, path, "listing_id", str),
    side: field(f, path, "side", oneOf(FILL_SIDES)),
    quantity: field(f, path, "quantity", nat),
    pricePaise: field(f, path, "price_paise", paise),
    feesPaise: field(f, path, "fees_paise", paise),
    evidence: field(f, path, "evidence", oneOf(EVIDENCE_TAGS)),
    sourceKind: field(f, path, "source_kind", oneOf(FILL_SOURCE_KINDS)),
    documentHash: field(f, path, "document_hash", matching(SHA256, "a SHA-256 digest")),
    operator: field(f, path, "operator", str),
    recordedAt: field(f, path, "recorded_at", instant),
    exchangeTime: field(f, path, "exchange_time", instant),
    brokerReference: field(f, path, "broker_reference", nullable(str)),
    reconciliation: field(f, path, "reconciliation", oneOf(RECONCILIATION_STATES)),
    statementId: field(f, path, "statement_id", nullable(str)),
    differences: field(f, path, "differences", listOf(decodeDifference)),
    version: field(f, path, "version", nat),
  };
  if (row.quantity === 0 || row.pricePaise === 0n) {
    refuse(path, "a positive quantity and price");
  }
  const expected =
    row.statementId === null
      ? "UNRECONCILED"
      : row.differences.length > 0
        ? "MISMATCHED"
        : "RECONCILED";
  if (row.reconciliation !== expected || (row.statementId === null && row.differences.length > 0)) {
    refuse(path, "a reconciliation state that agrees with its statement and differences");
  }
  if (row.exchangeTime > row.recordedAt) {
    refuse(path, "an exchange time no later than the recording");
  }
  return row;
}

/** Decode the `GET /api/v1/fills` answer. */
export function decodeFillListing(value: unknown, path = "fills"): readonly FillRecord[] {
  const f = exact(value, path, ["fills"]);
  return field(
    f,
    path,
    "fills",
    listOf((item, at) => decodeFillRecord(item, at)),
  );
}

// --- routing facts (INC-0807, P0-E; mirrors `scripts/ui_fixtures_routing.py`) ---

/**
 * The projection read for the halt, the cooldown, the modification counts and the confirmation
 * record: a `ReadEnvelope` like every other read (review F1), so its freshness gates the commands
 * it informs and its `snapshot_time` anchors the cooldown on the server's clock.
 */
export const ROUTING_FACTS = "routing_facts";

/** The halt an account is in, and the cooldown the server applies before a resume (WC-07). */
export interface HaltFacts {
  readonly haltedSince: string;
  readonly cooldownSeconds: number;
  readonly reviewRequired: boolean;
}

/**
 * The out-of-band confirmation record: for which action, how and until when; the code itself is
 * never in it (U049). A code is live only for its own action (build-log item 168).
 */
export interface OutOfBandFacts {
  readonly required: boolean;
  readonly action: CommandAction;
  readonly channel: string;
  readonly issuedAt: string;
  readonly expiresAt: string;
}

/** How many times one order has been modified. */
export interface ModificationCount {
  readonly orderId: string;
  readonly used: number;
}

/** The one row of the `routing_facts` projection for an account. */
export interface RoutingFacts {
  readonly halt: HaltFacts | null;
  readonly modificationCap: number;
  readonly modifications: readonly ModificationCount[];
  readonly outOfBand: OutOfBandFacts | null;
}

function decodeHalt(value: unknown, path: string): HaltFacts {
  const f = exact(value, path, ["halted_since", "cooldown_seconds", "review_required"]);
  return {
    haltedSince: field(f, path, "halted_since", instant),
    cooldownSeconds: field(f, path, "cooldown_seconds", nat),
    reviewRequired: field(f, path, "review_required", bool),
  };
}

function decodeOutOfBand(value: unknown, path: string): OutOfBandFacts {
  const f = exact(value, path, ["required", "action", "channel", "issued_at", "expires_at"]);
  const facts: OutOfBandFacts = {
    required: field(f, path, "required", bool),
    action: field(f, path, "action", oneOf(COMMAND_ACTIONS)),
    channel: field(f, path, "channel", str),
    issuedAt: field(f, path, "issued_at", instant),
    expiresAt: field(f, path, "expires_at", instant),
  };
  if (facts.expiresAt <= facts.issuedAt) {
    refuse(path, "a confirmation that expires after it was issued");
  }
  return facts;
}

function decodeModification(value: unknown, path: string): ModificationCount {
  const f = exact(value, path, ["order_id", "used"]);
  return {
    orderId: field(f, path, "order_id", str),
    used: field(f, path, "used", nat),
  };
}

/**
 * Decode a `routing_facts` row; a count above the cap contradicts the server's own rules and
 * fails closed. The confirmation record is the reader's own live code, independent of any halt. (A halt after the snapshot
 * is checked against the envelope by the page, which holds the snapshot time.)
 */
export function decodeRoutingFacts(value: unknown, path: string): RoutingFacts {
  const f = exact(value, path, ["halt", "modification_cap", "modifications", "out_of_band"]);
  const facts: RoutingFacts = {
    halt: field(f, path, "halt", nullable(decodeHalt)),
    modificationCap: field(f, path, "modification_cap", nat),
    modifications: field(f, path, "modifications", listOf(decodeModification)),
    outOfBand: field(f, path, "out_of_band", nullable(decodeOutOfBand)),
  };
  if (facts.modifications.some((count) => count.used > facts.modificationCap)) {
    refuse(path, "modification counts within the cap");
  }
  return facts;
}

/** The routing projection's row decoder, by projection name. */
export const ROUTING_ROWS: Readonly<Record<string, Reader<unknown>>> = {
  [ROUTING_FACTS]: decodeRoutingFacts,
};

/** The wire form of a confirmed resume: the request, its proof and the out-of-band confirmation. */
export interface ConfirmedRequest extends AuthorisedRequest {
  readonly code: string;
}

/** Decode the wire form of a confirmed resume (the nine keys, `authorisation`, `out_of_band`). */
export function decodeConfirmedRequest(value: unknown): ConfirmedRequest {
  const path = "request";
  const { out_of_band: confirmation, ...rest } = record(value, path);
  const f = exact(confirmation, `${path}.out_of_band`, ["code"]);
  return { ...decodeAuthorisedRequest(rest), code: field(f, `${path}.out_of_band`, "code", str) };
}

/** What an issued out-of-band code shows: when, until when and the redacted channel; never the code. */
export interface IssuedCode {
  readonly issuedAt: string;
  readonly expiresAt: string;
  readonly channel: string;
}

/** Decode the `201` of `POST /api/v1/commands/out-of-band`; any other field fails closed. */
export function decodeIssuedCode(value: unknown): IssuedCode {
  const path = "out_of_band_issue";
  const f = exact(value, path, ["issued_at", "expires_at", "channel"]);
  const issued: IssuedCode = {
    issuedAt: field(f, path, "issued_at", instant),
    expiresAt: field(f, path, "expires_at", instant),
    channel: field(f, path, "channel", str),
  };
  if (issued.expiresAt <= issued.issuedAt) {
    refuse(path, "a code that expires after it was issued");
  }
  return issued;
}
